/** Local-time YYYY-MM-DD (no timezone conversion — clinic-local date). */
export function toDateString(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export function startOfToday(): Date {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
}

/** Add minutes to a "HH:mm" time string, returning "HH:mm". */
export function addMinutesToTime(time: string, minutes: number): string {
  const [hStr, mStr] = time.split(':');
  const h = Number(hStr ?? 0);
  const m = Number(mStr ?? 0);
  const total = h * 60 + m + minutes;
  const newH = Math.floor(total / 60) % 24;
  const newM = total % 60;
  return `${String(newH).padStart(2, '0')}:${String(newM).padStart(2, '0')}`;
}

/** Build a Mon-first grid of dates (or null for padding) for the given month. */
export function buildMonthGrid(year: number, month: number): (Date | null)[] {
  const first = new Date(year, month - 1, 1);
  const last = new Date(year, month, 0);
  const startDay = (first.getDay() + 6) % 7; // Mon = 0
  const cells: (Date | null)[] = Array(startDay).fill(null);
  for (let d = 1; d <= last.getDate(); d++) cells.push(new Date(year, month - 1, d));
  while (cells.length % 7) cells.push(null);
  return cells;
}

const ES_MONTHS = [
  'enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
  'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre',
];

const ES_WEEKDAYS = [
  'domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado',
];

export function formatMonthYear(year: number, month: number): string {
  return `${ES_MONTHS[month - 1]} ${year}`;
}

export function formatLongDate(dateStr: string): string {
  const [y, m, d] = dateStr.split('-').map(Number);
  const date = new Date(y!, m! - 1, d!);
  const weekday = ES_WEEKDAYS[date.getDay()];
  return `${weekday} ${d} de ${ES_MONTHS[m! - 1]} de ${y}`;
}

/**
 * «08:30» → «8:30 a.m.», «16:00» → «4:00 p.m.».
 * La API y Sikumed siguen hablando en 24 horas; esto es solo lo que se ve.
 */
export function formatHora(hhmm: string): string {
  const [h, m] = hhmm.split(':');
  const hora = Number(h ?? 0);
  const minutos = m ?? '00';
  const sufijo = hora < 12 ? 'a.m.' : 'p.m.';
  const doce = hora % 12 === 0 ? 12 : hora % 12;
  return `${doce}:${minutos} ${sufijo}`;
}

export function formatShortDate(dateStr: string): string {
  const [y, m, d] = dateStr.split('-').map(Number);
  const date = new Date(y!, m! - 1, d!);
  const weekday = ES_WEEKDAYS[date.getDay()];
  return `${weekday} ${d} ${ES_MONTHS[m! - 1]}`;
}
